import { getAllCompositeSegments, getAllSegments, getClipFPS } from '@/player-config/src/utils/clipUtils';
import { computeCaptionsForComposite } from '@/App/remotion/libs/caption/caption';
import { DEFAULT_FONT_SIZE } from '@/player-config/src/constants/player';
import { CompositeSegment } from '@/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { SegmentType } from '@/player-config/src/types/Segment/Type';
import { Clip } from '@/player-config/src/types/clip-types';
import { CaptionStyles, CaptionStyle, CaptionLineConfig } from '@/player-config/src/types/asset-types';
import { Word } from '@/player-config/src/types/transcript-types';
import { isSplitsInTimelineEnabled } from '@/stores/clip';

export const updateCaptionPositions = (newClip: Clip) => {
  const clipLayout = newClip.asset_metadata.layout;
  const useRemotionCaptions = clipLayout !== 'AUDIOGRAM';
  const showCaptions = !!newClip.asset_metadata.subtitle;
  if (
    newClip.asset_metadata.caption_positions === undefined &&
    newClip.asset_metadata.caption_styles?.placement === undefined &&
    showCaptions &&
    useRemotionCaptions
  ) {
    newClip.asset_metadata.caption_positions = {
      x: '2.5%',
      y: '82%',
      width: '95%',
      height: '18%'
    } as any;
  }
};

export const updateCaptions = (newClip: Clip) => {
  const allCompositeSegments = getAllCompositeSegments(newClip);
  allCompositeSegments.forEach(compositeSegment => {
    if (!compositeSegment.segment_metadata) {
      throw new Error('Composite segment metadata not found');
    }
    const result = computeCaptionsForComposite(compositeSegment, newClip);
    if (!result) {
      throw new Error('Failed to compute captions for composite segment');
    }
    compositeSegment.segment_metadata.captions = result.captions;
    compositeSegment.segment_metadata.excludes = result.excludes;
    compositeSegment.segment_metadata.duration = result.duration;
    compositeSegment.segment_metadata.captions_lang =
      compositeSegment.segment_metadata.captions_lang || newClip.asset_metadata.original_lang;
  });
};

const updateGlobalCaptionsForSegment = (segment: CompositeSegment, offset: number, indexCount: number) => {
  const captions = segment.segment_metadata?.captions || [];
  const updatedCaptions: Word[] = [];
  let newIndexCount = indexCount;
  for (const caption of captions) {
    updatedCaptions.push({
      ...caption,
      index: newIndexCount,
      processed_start_time: (caption.processed_start_time || 0) + offset,
      processed_end_time: (caption.processed_end_time || 0) + offset
    });
    newIndexCount++;
  }
  return updatedCaptions;
};

// This function is to update captions for the entire clip for handling splits and transcript downloads.
// This needs to be called after the captions are updated for the composite segments.
export const updateGlobalCaptions = (newClip: Clip) => {
  const isSplitsEnabled = isSplitsInTimelineEnabled(newClip);
  if (!isSplitsEnabled) {
    return;
  }
  const allSegments = getAllSegments(newClip);
  const globalCaptions: Word[] = [];
  let offset = 0;
  let indexCount = 0;
  for (const segment of allSegments) {
    if (segment.type === SegmentType.COMPOSITE) {
      globalCaptions.push(...updateGlobalCaptionsForSegment(segment, offset, indexCount));
      offset += segment.segment_metadata?.duration || 0;
      indexCount = globalCaptions.length;
    } else if (segment.type === SegmentType.VIDEO) {
      offset += (segment.toFrame - segment.fromFrame) / getClipFPS(newClip);
    } else {
      throw new Error('Segment type not implemented');
    }
  }
  newClip.asset_metadata.captions = globalCaptions;
};

export const getDefaultCaptionStyles = (newClip: Clip): CaptionStyles => {
  return {
    scale: 1,
    font_size: DEFAULT_FONT_SIZE,
    caps: false,
    line_config: newClip.asset_metadata.caption_styles?.line_config || CaptionLineConfig.OneLine,
    animation: newClip.asset_metadata.caption_styles?.animation || CaptionStyle.Outline
  };
};
